'use client';

/**
 * Salonomy web UI primitives. Token-driven (see ./styles.css → @salonomy/design-tokens).
 * NOTE: interactive (hooks: useId/useRef/useFocusTrap) → this module is a CLIENT boundary, required by
 * the Next RSC consumer (apps/marketing pulls @salonomy/ui via its barrel). The directive is a harmless
 * no-op for the Vite/React-Router console SPA.
 * Premium-editorial brand: Instrument Serif display, Instrument Sans body, Spline Sans Mono
 * labels/numbers; ink/porcelain with a single cobalt accent and pill-shaped controls.
 */
import type {
  ButtonHTMLAttributes,
  InputHTMLAttributes,
  ReactNode,
  SelectHTMLAttributes,
  TextareaHTMLAttributes,
} from 'react';
import { useId, useRef } from 'react';
import { useFocusTrap } from './useFocusTrap.js';

export function cx(...parts: Array<string | false | null | undefined>): string {
  return parts.filter(Boolean).join(' ');
}

// ── Buttons ──
export type ButtonVariant = 'primary' | 'outline' | 'ghost' | 'danger' | 'soft';
export type ButtonSize = 'sm' | 'md' | 'lg';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  block?: boolean;
}

export function Button({
  variant = 'primary',
  size = 'md',
  block,
  className,
  type = 'button',
  ...rest
}: ButtonProps) {
  return (
    <button
      type={type}
      className={cx(
        'slm-btn',
        `slm-btn--${variant}`,
        `slm-btn--${size}`,
        block && 'slm-btn--block',
        className,
      )}
      {...rest}
    />
  );
}

export interface IconButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  label: string;
}

export function IconButton({
  label,
  className,
  type = 'button',
  children,
  ...rest
}: IconButtonProps) {
  return (
    <button type={type} aria-label={label} className={cx('slm-iconbtn', className)} {...rest}>
      {children}
    </button>
  );
}

// ── Surfaces ──
export interface CardProps {
  children: ReactNode;
  className?: string;
  as?: 'div' | 'section' | 'aside' | 'article';
  padded?: boolean;
}

export function Card({ children, className, as: Tag = 'div', padded = true }: CardProps) {
  return <Tag className={cx('slm-card', padded && 'slm-card--padded', className)}>{children}</Tag>;
}

// ── Badges / pills ──
export function Badge({ children, className }: { children: ReactNode; className?: string }) {
  return <span className={cx('slm-badge', className)}>{children}</span>;
}

export type PillTone = 'neutral' | 'cobalt' | 'success' | 'danger' | 'soft';

export function Pill({
  children,
  tone = 'neutral',
  className,
}: {
  children: ReactNode;
  tone?: PillTone;
  className?: string;
}) {
  return <span className={cx('slm-pill', `slm-pill--${tone}`, className)}>{children}</span>;
}

// ── Toggle ──
/**
 * A switch control. When `label` is given it renders beside the switch and is part of the click
 * target — clicking the label text toggles. (Previously `label` was only the aria-label and callers
 * rendered their own adjacent text, which a native `<label>` could not forward to this `<button>`.)
 * `labelHidden` keeps the old behaviour — `label` becomes the accessible name only, nothing visible —
 * for the few places that draw their own descriptive text.
 */
export function Toggle({
  checked,
  onChange,
  label,
  labelHidden,
  disabled,
  className,
}: {
  checked: boolean;
  onChange: (next: boolean) => void;
  label?: string;
  /** Render `label` as the accessible name only (no visible text) — caller supplies its own copy. */
  labelHidden?: boolean;
  disabled?: boolean;
  className?: string;
}) {
  const showLabel = Boolean(label) && !labelHidden;
  const button = (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={showLabel ? undefined : label}
      disabled={disabled}
      // Stop the click bubbling to the wrapper so a press on the switch itself toggles exactly once
      // (the wrapper's handler covers presses on the label text).
      onClick={(e) => {
        e.stopPropagation();
        onChange(!checked);
      }}
      className={cx('slm-toggle', checked && 'slm-toggle--on')}
    >
      <span className="slm-toggle__knob" />
    </button>
  );
  if (!showLabel) return className ? <span className={className}>{button}</span> : button;
  return (
    <span
      className={cx('slm-toggle-field', disabled && 'slm-toggle-field--disabled', className)}
      onClick={() => {
        if (!disabled) onChange(!checked);
      }}
    >
      {button}
      <span className="slm-toggle-field__label">{label}</span>
    </span>
  );
}

// ── Avatar ──
export type AvatarTone = 'ink' | 'cobalt' | 'slate' | 'muted';

export function Avatar({
  initials,
  tone = 'ink',
  size = 34,
  className,
}: {
  initials: string;
  tone?: AvatarTone;
  size?: number;
  className?: string;
}) {
  return (
    <span
      className={cx('slm-avatar', `slm-avatar--${tone}`, className)}
      style={{ width: size, height: size, fontSize: Math.round(size * 0.36) }}
      aria-hidden
    >
      {initials}
    </span>
  );
}

// ── Segmented control ──
export interface SegmentOption<T extends string> {
  value: T;
  label: string;
}

export function SegmentedControl<T extends string>({
  options,
  value,
  onChange,
}: {
  options: ReadonlyArray<SegmentOption<T>>;
  value: T;
  onChange: (v: T) => void;
}) {
  return (
    <div className="slm-segment" role="tablist">
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          role="tab"
          aria-selected={o.value === value}
          className={cx('slm-segment__item', o.value === value && 'slm-segment__item--on')}
          onClick={() => onChange(o.value)}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

// ── Form controls ──
export function Field({
  label,
  hint,
  error,
  children,
  htmlFor,
}: {
  label: string;
  hint?: string;
  error?: string | null;
  children: ReactNode;
  htmlFor?: string;
}) {
  return (
    <label className="slm-field" htmlFor={htmlFor}>
      <span className="slm-field__label">{label}</span>
      {children}
      {error ? (
        <span className="slm-field__error">{error}</span>
      ) : hint ? (
        <span className="slm-field__hint">{hint}</span>
      ) : null}
    </label>
  );
}

export function TextInput({ className, ...rest }: InputHTMLAttributes<HTMLInputElement>) {
  return <input className={cx('slm-input', className)} {...rest} />;
}

export function TextArea({ className, ...rest }: TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return <textarea className={cx('slm-input', 'slm-input--area', className)} {...rest} />;
}

export function Select({ className, children, ...rest }: SelectHTMLAttributes<HTMLSelectElement>) {
  return (
    <select className={cx('slm-input', 'slm-select', className)} {...rest}>
      {children}
    </select>
  );
}

// ── Stat (mono label + serif value) ──
export function Stat({
  label,
  value,
  sub,
  className,
}: {
  label: string;
  value: ReactNode;
  sub?: ReactNode;
  className?: string;
}) {
  return (
    <div className={cx('slm-stat', className)}>
      <span className="slm-stat__label">{label}</span>
      <span className="slm-stat__value">{value}</span>
      {sub ? <span className="slm-stat__sub">{sub}</span> : null}
    </div>
  );
}

// ── Spinner (used only off the critical path; never gates a till action) ──
export function Spinner({ size = 18 }: { size?: number }) {
  return (
    <span className="slm-spinner" style={{ width: size, height: size }} aria-label="Loading" />
  );
}

// ── Modal ──
export function Modal({
  open,
  onClose,
  title,
  children,
  footer,
  width = 460,
}: {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  width?: number;
}) {
  const titleId = useId();
  const panelRef = useRef<HTMLDivElement>(null);
  // Trap focus inside the dialog while open, close on Escape, and restore focus to the opener on
  // close. The shared @salonomy/ui trap; the backdrop onMouseDown-to-close behaviour is untouched.
  useFocusTrap(panelRef, open, onClose);
  if (!open) return null;
  return (
    <div
      className="slm-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      onMouseDown={onClose}
    >
      <div
        ref={panelRef}
        className="slm-modal__panel"
        style={{ width }}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="slm-modal__head">
          <div className="slm-modal__title" id={titleId}>
            {title}
          </div>
          <IconButton label="Close" onClick={onClose} className="slm-modal__close">
            ×
          </IconButton>
        </div>
        <div className="slm-modal__body">{children}</div>
        {footer ? <div className="slm-modal__foot">{footer}</div> : null}
      </div>
    </div>
  );
}

// ── Empty state ──
export function EmptyState({
  title,
  description,
  action,
}: {
  title: string;
  description?: string;
  action?: ReactNode;
}) {
  return (
    <div className="slm-empty">
      <div className="slm-empty__title">{title}</div>
      {description ? <div className="slm-empty__desc">{description}</div> : null}
      {action ? <div className="slm-empty__action">{action}</div> : null}
    </div>
  );
}
